import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO, SERVICES } from "../data";

export default function Gulvloesninger() {
  const heroImg = "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/hero-epoxy-floor.jpg";

  return (
    <>
      <Helmet>
        <title>Gulvløsninger i Halsnæs | Epoxy, PU & Industrigulve</title>
        <meta
          name="description"
          content="Se vores brede udvalg af fugefrie gulvløsninger i Halsnæs. Vi tilbyder epoxy, polyurethan, kvartsgulve og flydemørtel til alle behov."
        />
        <meta property="og:title" content="Gulvløsninger i Halsnæs | Epoxy, PU & Industrigulve" />
        <meta
          property="og:description"
          content="Se vores brede udvalg af fugefrie gulvløsninger i Halsnæs. Vi tilbyder epoxy, polyurethan, kvartsgulve og flydemørtel til alle behov."
        />
        <meta property="og:image" content={heroImg} />
      </Helmet>

      {/* Page Header */}
      <section className="bg-brand-blue-900 text-white pt-48 pb-28 sm:pt-44 sm:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={heroImg}
            alt="Gulvløsninger baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-blue-300 mb-3">
              Ydelser
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Vores Gulvløsninger
            </h1>
            <p className="text-lg text-brand-blue-100 mt-4 max-w-2xl">
              Vi tilbyder et bredt udvalg af fugefrie gulvbelægninger i epoxy og polyurethan, der tilpasses præcist til dit projekt.
            </p>
          </div>
        </div>
      </section>

      {/* Overview Grid */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl font-extrabold text-brand-blue-900 tracking-tight">
              Skræddersyede gulvbelægninger til ethvert formål
            </h2>
            <p className="text-base text-concrete-600">
              Uanset om du skal bruge et gulv til en tungt belastet industrivirksomhed, et repræsentativt showroom, et autoværksted eller et moderne hjem, har vi den rette løsning.
            </p>
          </div>

          <div className="space-y-16">
            {SERVICES.map((service, idx) => (
              <div
                key={idx}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${
                  idx % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Image (5 cols) */}
                <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-last" : ""}`}>
                  <div className="aspect-4/3 overflow-hidden rounded-2xl shadow-lg bg-concrete-100 border border-concrete-100">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Copy (7 cols) */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-blue-50 text-brand-blue-700 text-lg">
                      <i className={service.icon}></i>
                    </span>
                    <h3 className="text-2xl font-bold text-brand-blue-900">{service.title}</h3>
                  </div>
                  <p className="text-base text-concrete-600 leading-relaxed">
                    {service.longDesc}
                  </p>

                  <div className="space-y-2.5">
                    <h4 className="font-bold text-sm text-brand-blue-900 uppercase tracking-wider">Nøglefordele:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.benefits.map((b, bIdx) => (
                        <div key={bIdx} className="flex items-center gap-2 text-sm text-concrete-700">
                          <i className="fa-solid fa-circle-check text-brand-blue-600 text-xs"></i>
                          <span>{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Link
                      to={`/${service.slug}`}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-blue-900 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-brand-blue-800"
                    >
                      Læs mere om {service.title.toLowerCase()}
                      <i className="fa-solid fa-arrow-right text-xs"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/hero-epoxy-floor.jpg"
            alt="Professionel gulvrådgivning"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Er du i tvivl om, hvilken gulvløsning du skal vælge?
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Valget af gulv afhænger af many faktorer som mekanisk belastning, kemisk påvirkning, rengøringsvenlighed, akustik og æstetik. Simon Babiel rådgiver dig gerne uforpligtende, så du får en løsning, der holder i mange år.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
            >
              Få professionel rådgivning
            </Link>
            <a
              href={`tel:+45${CONTACT_INFO.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-white/10 transition-all"
            >
              <i className="fa-solid fa-phone"></i>
              Ring til Simon
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
